<template>
  <v-sheet class="pa-4 mx-auto overflow-y-auto" elevation="1" max-width="800" rounded width="100%"
    :max-height="cardHeight">
    <v-form v-model="valid" :disabled="isViewer || isAdmin">
      <v-container :max-height="cardHeight">
        <v-row>
          <v-col cols="12">
            <h6 class="text-overline mx-2">{{ $t("labels.connection") }}</h6>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="_measure.name" :counter="20" :rules="nameRules" :label="$t('labels.name')" required
              variant="outlined" density="compact" hide-details="auto"></v-text-field>
          </v-col>
          <v-col cols="6" :md="selectedType == 'influx' ? 4 : 3">
            <v-select
              :disabled="measureId ? true : false"
              v-model="selectedType"
              :items="types"
              :rules="requiredRule"
              :label="$t('labels.type')"
              required
              variant="outlined"
              density="compact"
              item-title="title"
              hide-details="auto"
              return-object
            ></v-select>
          </v-col>
          <v-col cols="6" md="4" v-if="selectedType == 'influx'">
            <v-select
              :key="versionSelectKey"
              v-model="influxVersionModel"
              :items="influxVersionItems"
              :label="$t('labels.version')"
              required
              variant="outlined"
              density="compact"
              :rules="requiredRule"
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="6" :md="selectedType == 'influx' ? 2 : 5" v-if="isAdmin">
            <v-select
              :disabled="contextTypes.length <= 1"
              v-model="_measure.context"
              :items="contextTypes"
              :rules="requiredRule"
              :label="$t('labels.context')"
              required
              variant="outlined"
              density="compact"
              :hide-details="'auto'"
              return-object
            ></v-select>
          </v-col>
          <v-col cols="12" md="12">
            <v-textarea v-model="_measure.description" :label="$t('labels.description')" row-height="15" rows="1"
              variant="outlined" auto-grow density="compact" hide-details="auto"></v-textarea>
          </v-col>
          <v-col cols="6" md="3" v-if="selectedType == 'rest'">
            <v-select v-model="_measure.source.spec.method" :items="methods" :rules="requiredRule"
              :label="$t('labels.method')" required variant="outlined" density="compact" hide-details="auto"></v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-select v-model="protocol" :items="protocols" :rules="requiredRule" :label="$t('labels.protocol')"
              required variant="outlined" density="compact" hide-details="auto"></v-select>
          </v-col>
          <v-col cols="6" md="3" v-if="selectedType != 'rest'">
            <v-text-field v-model="_measure.source.port" label="Port" variant="outlined" density="compact"
              hide-details="auto"></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="uriWithPrefix" label="URL" required variant="outlined" density="compact"
              :rules="requiredRule" hide-details="auto" :prefix="uriPrefix(selectedType)"></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-switch v-model="switchStatus"
              :label="`${$t('strings.private-connection')} ${switchStatus ? $t('labels.yes') : $t('labels.no')}`"
              hide-details="auto"
              color="secondary"
              @click="toggleSwitch"
              :disabled="switchDisabled"
            ></v-switch>
            <p v-if="switchStatus" class="text-caption text-primary">
              {{ $t("strings.private-advice-policy") }}
            </p>
          </v-col>
          <v-col cols="6" class="d-flex align-center">
            <v-row v-if="switchStatus" dense>
              <v-col cols="12" md="12" v-if="!isInflux || isInfluxV1">
                <v-text-field
                  v-model="_measure.source.auth.username"
                  :label="$t('labels.username')"
                  :rules="!isInflux && switchStatus ? requiredRule : []"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="12">
                <v-text-field
                  v-if="!isInflux"
                  v-model="_measure.source.auth.accessKey"
                  :label="$t('labels.access-Key')"
                  :rules="switchStatus ? requiredRule : []"
                  :append-inner-icon="
                    passwordVisible ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :type="passwordVisible ? 'text' : 'password'"
                  density="compact"
                  placeholder="Enter your password"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  hide-details="auto"
                  @click:append-inner="passwordVisible = !passwordVisible"
                ></v-text-field>
                <v-text-field
                  v-if="isInflux"
                  v-model="_measure.source.auth.accessKey"
                  :label="isInfluxV1 ? $t('labels.password') : $t('labels.token')"
                  :append-inner-icon="tokenVisible ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="tokenVisible ? 'text' : 'password'"
                  density="compact"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  hide-details="auto"
                  :rules="isInfluxV1 ? [] : requiredRule"
                  @click:append-inner="tokenVisible = !tokenVisible"
                ></v-text-field>
                <p
                  v-if="isInflux && !isInfluxV1"
                  class="text-caption text-primary mt-1"
                >
                  {{ $t("strings.influx-token-warning") }}
                </p>
              </v-col>
            </v-row>
            <p v-else class="text-caption text-primary">
              {{ $t("strings.private-message") }}
            </p>
          </v-col>
          <v-divider class="mb-4"></v-divider>
          <v-col cols="12">
            <v-row v-if="selectedType == 'rest'" class="px-0">
              <v-col cols="12" md="12">
                <h6 class="text-overline mx-2 mb-4">Headers</h6>
                <v-row v-for="(header, index) in headersRow" :key="index" class="mb-2" dense>
                  <v-col cols="5">
                    <v-text-field v-model="header.key" @input="onKeyChange(index, $event.target.value)" label="Key"
                      variant="outlined" density="compact" hide-details></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="header.value" @input="onValueChange(index, $event.target.value)"
                      label="Value" color="primary" variant="outlined" density="compact" hide-details></v-text-field>
                  </v-col>
                  <v-col cols="1" class="d-flex justify-center">
                    <v-btn @click="removeRow(index)" color="secondary" class="my-1" icon="mdi-delete" variant="text"
                      size="default" :ripple="false" density="compact" :disabled="isViewer || header.key && header.value && headersRow.length <= 1
                        ">
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="12" v-if="_measure.source.spec.method == 'POST'">
                <v-textarea v-model="_measure.source.spec.body" label="Body" hide-details="auto" row-height="15"
                  rows="1" variant="outlined" density="compact" auto-grow></v-textarea>
              </v-col>
            </v-row>

            <v-row v-else class="px-0">
              <v-col cols="12" md="6" v-if="_measure.source.type !== 'influx'">
                <v-text-field
                  v-model="_measure.source.auth.databaseName"
                  :label="$t('labels.database-name')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="isInflux && !isInfluxV2">
                <v-text-field
                  v-model="_measure.source.auth.databaseName"
                  :label="$t('labels.database-name')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="isInfluxV2">
                <v-text-field
                  v-model="_measure.source.spec.organization"
                  :label="$t('labels.organization')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="isInfluxV2">
                <v-text-field
                  v-model="_measure.source.spec.bucket"
                  :label="$t('labels.bucket')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="isFluxLanguage ? [] : requiredRule"
                  :disabled="isFluxLanguage"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" v-if="isInflux">
                <v-select
                  v-model="_measure.source.spec.influxQueryLanguage"
                  :items="availableInfluxQueryLanguages"
                  :label="$t('labels.query-language')"
                  required
                  variant="outlined"
                  density="compact"
                  :rules="requiredRule"
                  :disabled="isInfluxLanguageFixed"
                  hide-details="auto"
                ></v-select>
              </v-col>
              <v-col cols="12" md="12" v-if="_measure.source.type == 'mongodb'">
                <v-text-field v-model="_measure.source.spec.collectionName" :label="$t('labels.collection-name')"
                  required variant="outlined" density="compact" :rules="requiredRule"
                  hide-details="auto"></v-text-field>
              </v-col>
              <v-col cols="12" md="12" v-else-if="_measure.source.type == 'graphdb'">
                <v-textarea v-model="_measure.source.spec.query" label="Query" required row-height="15" rows="1"
                  variant="outlined" density="compact" auto-grow :rules="requiredRule" hide-details="auto"></v-textarea>
              </v-col>
              <v-col cols="12" md="12" v-else-if="_measure.source.type == 'neo4j'">
                <v-textarea v-model="queryWithMatchPrefix" label="Query" required row-height="15" rows="1"
                  variant="outlined" density="compact" auto-grow :rules="requiredRule" hide-details="auto"
                  prefix="MATCH" placeholder="... RETURN..."></v-textarea>
              </v-col>
              <v-col
                cols="12"
                md="12"
                v-else-if="_measure.source.type == 'influx'"
              >
                <v-textarea
                  v-model="_measure.source.spec.query"
                  :label="$t('labels.query')"
                  required
                  row-height="15"
                  rows="1"
                  variant="outlined"
                  density="compact"
                  auto-grow
                  :rules="requiredRule"
                  hide-details="auto"
                  :placeholder="influxQueryPlaceholder"
                ></v-textarea>
                <p
                  v-if="fluxQueryIncompatible"
                  class="text-caption text-error mt-1"
                >
                  {{
                    $t("strings.influx-flux-incompatible", {
                      version: (_measure.source.spec.influxVersion || "").toUpperCase(),
                    })
                  }}
                </p>
                <p
                  v-else-if="isInfluxV2 && isFluxLanguage"
                  class="text-caption text-primary mt-1"
                >
                  {{
                    bucketFromQuery
                      ? $t("strings.influx-bucket-detected", { bucket: bucketFromQuery })
                      : $t("strings.influx-bucket-in-query-hint")
                  }}
                </p>
              </v-col>
              <v-col cols="12" md="12" v-else>
                <v-textarea
                  v-model="queryWithSelectPrefix"
                  :label="$t('labels.query')"
                  required
                  row-height="15"
                  rows="1"
                  variant="outlined"
                  density="compact"
                  auto-grow
                  :rules="requiredRule"
                  hide-details="auto"
                  prefix="SELECT"
                  placeholder="FROM ... WHERE ..."
                ></v-textarea>
              </v-col>
            </v-row>
          </v-col>
          <v-divider class="mt-4" v-if="selectedType == 'rest' || selectedType == 'mongodb'"></v-divider>
          <v-col cols="12" md="12" v-if="selectedType == 'rest' || selectedType == 'mongodb'">
            <h6 class="text-overline mx-2 mb-4">
              {{ $t("labels.path-data") }}
            </h6>
            <p class="text-caption text-primary mt-4 mb-4">
              {{ $t("strings.compile-json-path") }}
            </p>
            <v-text-field v-model="jsonPathWithPrefix" :disabled="disabledJsonPath" readonly label="JsonPath"
              variant="outlined" density="compact" hide-details="auto" prefix="$.">
              <template #append-inner>
                <!-- Validation spinner -->
                <v-progress-circular v-if="jsonPathValidating" indeterminate size="18" width="2" color="primary"
                  class="mr-1"></v-progress-circular>
                <!-- Rate-limited: lets the user retry right away instead of leaving the form -->
                <v-tooltip v-if="!jsonPathValidating && jsonPathRateLimited" location="bottom">
                  <template #activator="{ props }">
                    <v-btn v-bind="props" icon size="x-small" variant="text" color="warning" class="mr-1"
                      @click="validateJsonPath">
                      <v-icon size="large">mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('errors.rate-limit.message-msg') }}</span>
                </v-tooltip>
                <!-- Validity icon: shown only when jsonPath is non-empty and not currently validating -->
                <v-tooltip v-if="jsonPathWithPrefix && !jsonPathValidating && jsonPathValidationStatus !== null"
                  location="bottom">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" :color="jsonPathValidationStatus ? 'success' : 'error'" class="mr-1">
                      {{ jsonPathValidationStatus ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                  </template>
                  <span>{{ jsonPathValidationStatus ? $t('strings.jsonpath-valid') : $t('errors.jsonpath-invalid')
                    }}</span>
                </v-tooltip>
                <!-- Open JSON property dialog button -->
                <v-tooltip location="bottom">
                  <template #activator="{ props }">
                    <v-btn icon size="x-small" color="primary" v-bind="props" :disabled="disabledJsonPath || isViewer"
                      @click="openDialogJsonProperty">
                      <v-icon size="large">mdi-code-json</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("strings.json-property") }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <DialogConfirmJsonpathDeletion :dialog="dialogConfirmJsonpathDeletion" @deleteConfirm="deleteJsonpathConfirm"
      @closeDialog="
        dialogConfirmJsonpathDeletion = !dialogConfirmJsonpathDeletion
      "
    />
    <v-dialog v-model="versionChangeDialog" max-width="500px" persistent>
      <v-card>
        <v-toolbar flat color="secondary">
          <v-toolbar-title class="text-white">
            {{ $t("strings.influx-version-change-title") }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="white" @click="cancelVersionChange">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <div class="d-flex align-start mt-2">
            <v-icon color="info" class="me-3 mt-1">mdi-information-outline</v-icon>
            <div>
              <p class="text-body-1 mb-3">
                {{ $t("strings.influx-version-change-intro") }}
              </p>
              <div class="d-flex flex-wrap ga-2 mb-4">
                <v-chip
                  v-for="field in pendingClearFieldLabels"
                  :key="field"
                  color="info"
                  variant="tonal"
                  size="small"
                  label
                >
                  {{ field }}
                </v-chip>
              </div>
              <p class="text-body-1 font-weight-medium mb-0">
                {{ $t("strings.influx-version-change-confirm") }}
              </p>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary darken-1" variant="text" @click="cancelVersionChange">
            {{ $t("labels.cancel") }}
          </v-btn>
          <v-btn color="secondary darken-1" variant="text" @click="confirmVersionChange">
            {{ $t("labels.continue") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import axios from "axios";
import DialogConfirmJsonpathDeletion from "./DialogConfirmJsonpathDeletion.vue";

const INFLUX_VERSIONS = Object.freeze(["v1", "v2", "v3"]);
const INFLUX_QUERY_LANGUAGE_COMPATIBILITY = Object.freeze({
  v1: ["InfluxQL"],
  v2: ["Flux", "InfluxQL"],
  v3: ["InfluxQL"],
});
const INFLUX_VERSION_TRANSITIONS = Object.freeze({
  "v1->v2": ["auth.username", "auth.databaseName"],
  "v1->v3": ["auth.username"],
  "v2->v1": ["spec.organization", "spec.bucket"],
  "v2->v3": ["spec.organization", "spec.bucket"],
  "v3->v1": [],
  "v3->v2": ["auth.databaseName"],
});
const INFLUX_BLOCKED_FIELDS = Object.freeze({
  v1: ["spec.organization", "spec.bucket"],
  v2: ["auth.username", "auth.databaseName"],
  v3: ["auth.username", "spec.organization", "spec.bucket"],
});
const INFLUX_QUERY_PLACEHOLDERS = Object.freeze({
  Flux: 'from(bucket: "kpi") |> range(start: -1h) |> filter(fn: (r) => r._measurement == "temperature")',
  InfluxQL: 'SELECT mean("temperature") FROM "kpi" WHERE time > now() - 1h',
});

export default {
  props: [
    "measure",
    "measureTypes",
    "containerHeight",
    "measureId",
    "contexts",
    "isAdmin",
    "isEditor",
    "isViewer",
    "apiUrl",
    "realm"
  ],
  components: { DialogConfirmJsonpathDeletion },
  data: function () {
    return {
      valid: true,
      passwordVisible: false,
      tokenVisible: false,
      methods: ["GET", "POST"],
      selectedMethod: null,
      nameRules: [
        (value) => {
          if (value) return true;
          return this.$t("strings.name-required");
        },
        (value) => {
          if (value?.length <= 20) return true;
          return this.$t("strings.name-less-20-chars");
        },
      ],
      requiredRule: [
        (value) => {
          if (value) return true;
          return this.$t("strings.value-required");
        },
      ],
      headersRow: [{ key: "", value: "" }],
      switchStatus: false,
      protocol: "HTTPS",
      protocols: ["HTTPS", "HTTP"],
      dialogConfirmJsonpathDeletion: false,
      confirmJsonpathDeletion: undefined,
      isInitialized: false,
      // JsonPath validation state
      jsonPathValidationStatus: null, // null = not checked, true = valid, false = invalid
      jsonPathValidating: false,
      jsonPathValidationTimer: null,
      jsonPathRateLimited: false, // true when the last validation call got a 429: shows a retry button
      versionChangeDialog: false,
      pendingInfluxVersion: null,
      pendingClearFields: [],
      versionSelectKey: 0,
    };
  },

  mounted() {
    this.setCredentialsSwitchStatus();
    this.checkFormValidity();
    this.$nextTick(() => {
      this.isInitialized = true;
      // If editing an existing measure with a jsonPath already set, validate it immediately
      if (this.measureId && this.jsonPathWithPrefix) {
        this.validateJsonPath();
      }
    });
  },

  watch: {
    "$i18n.locale": {
      handler: function () {
        if (!this._measure.id) {
          this._measure.name = this.$t("strings.new-measure");
        }
      },
      immediate: true,
    },

    protocol(newProtocol) {
      if (this._measure.source.uri) {
        const cleanedUri = this._measure.source.uri.replace(
          /^(http|https|mysql|postgres|bolt|bolt\+s|neo4j|neo4j\+s|mongodb):\/\//,
          "",
        );
        this._measure.source.uri = `${newProtocol.toLowerCase()}://${cleanedUri}`;
      }
      this.confirmJsonpathDeletion = undefined;
      this.checkMandatoryFieldsForJsonpath();
    },

    measure: {
      handler() {
        if (
          this._measure.source.spec.headers &&
          this._measure.source.spec.headers !== "" &&
          this._measure.id
        ) {
          this.parseHeaders(this._measure.source.spec.headers);
        }
      },
      immediate: true,
      deep: true,
    },

    selectedType(newType, oldType) {
      this.setProtocols(newType);
      if (newType !== oldType) {
        if (newType == "rest" || newType == "mongodb") {
          this.checkMandatoryFieldsForJsonpath();
        } else {
          this.jsonPathWithPrefix = "";
        }

        if (newType !== "rest") {
          delete this._measure.source.spec.method;
          this._measure.source.uri = "";
          this.headersRow = [{ key: "", value: "" }];
        }
        if (newType !== "mongodb") {
          delete this._measure.source.spec.jsonPath;
          this._measure.source.auth.databaseName = "";
          this._measure.source.spec.collectionName = "";
        }
        if (newType !== "influx") {
          delete this._measure.source.spec.influxVersion;
          delete this._measure.source.spec.influxQueryLanguage;
        }
        if (newType == "influx") {
          this.normalizeInfluxSelection();
          this.enforceInfluxSwitch();
          this.checkFormValidity();
        }

        // Reset validation when type changes
        this.jsonPathValidationStatus = null;
      }
    },

    "_measure.name": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.context": function () {
      this.checkMandatoryFieldsForJsonpath();
    },
    "_measure.source.spec.method": function () {
      this.confirmJsonpathDeletion = undefined;
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.source.uri": function () {
      this.checkFormValidity();
      // Re-validate jsonPath if URI changes and jsonPath is already set
      if (this.jsonPathWithPrefix) {
        this.jsonPathValidationStatus = null;
        this._scheduleJsonPathValidation();
      }
    },
    "_measure.source.auth.username": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.source.auth.accessKey": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.source.auth.databaseName": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    "_measure.source.spec.query": function () {
      this.checkFormValidity();
    },
    "_measure.source.spec.organization": function () {
      this.checkFormValidity();
    },
    "_measure.source.spec.bucket": function () {
      this.checkFormValidity();
    },
    "_measure.source.spec.influxVersion": function () {
      this.normalizeInfluxSelection();
      this.enforceInfluxSwitch();
    },
    "_measure.source.spec.influxQueryLanguage": function (newLang) {
      if (newLang === "Flux") {
        this._measure.source.spec.bucket = "";
      }
      this.checkFormValidity();
    },
    "_measure.source.spec.collectionName": function () {
      this.checkMandatoryFieldsForJsonpath();
      this.checkFormValidity();
    },
    uriWithPrefix: function () {
      this.confirmJsonpathDeletion = undefined;
      this.checkMandatoryFieldsForJsonpath();
    },
    contextTypes: {
      handler(newContexts) {
        if (newContexts && newContexts.length > 0) {
          this.setDefaultContextForEditor();
        }
      },
      immediate: true,
    },

    // Watch jsonPathWithPrefix to trigger validation whenever it changes
    jsonPathWithPrefix(newVal) {
      if (!newVal) {
        // Empty jsonPath: reset status and re-check form (preview can still be enabled without jsonPath)
        this.jsonPathValidationStatus = null;
        this.checkFormValidity();
        return;
      }
      // Non-empty: schedule debounced validation
      this._scheduleJsonPathValidation();
    },
    switchStatus() {
      this.checkFormValidity();
    }
  },

  computed: {
    types() {
      return this.measureTypes || [];
    },
    contextTypes() {
      return this.contexts || [];
    },
    influxVersionItems() {
      return INFLUX_VERSIONS.map((v) => ({ title: v.toUpperCase(), value: v }));
    },
    influxVersionModel: {
      get() {
        return this._measure.source.spec.influxVersion;
      },
      set(newVersion) {
        this.requestInfluxVersionChange(newVersion);
      },
    },
    isInflux() {
      return this.selectedType === "influx";
    },
    influxVersion() {
      return this._measure.source.spec.influxVersion;
    },
    isInfluxV1() {
      return this.isInflux && this.influxVersion === "v1";
    },
    isInfluxV2() {
      return this.isInflux && this.influxVersion === "v2";
    },
    isInfluxV3() {
      return this.isInflux && this.influxVersion === "v3";
    },
    isFluxLanguage() {
      return this._measure.source.spec.influxQueryLanguage === "Flux";
    },
    isInfluxLanguageFixed() {
      return this.isInflux && (this.isInfluxV1 || this.isInfluxV3);
    },
    credentialsMandatory() {
      return this.isInflux && (this.isInfluxV2 || this.isInfluxV3);
    },
    switchDisabled() {
      return this.isViewer || this.credentialsMandatory;
    },
    availableInfluxQueryLanguages() {
      return INFLUX_QUERY_LANGUAGE_COMPATIBILITY[this._measure.source.spec.influxVersion] || [];
    },
    influxQueryPlaceholder() {
      return INFLUX_QUERY_PLACEHOLDERS[this._measure.source.spec.influxQueryLanguage] || "";
    },
    fluxQueryIncompatible() {
      if (!this.isInflux || !this.isInfluxLanguageFixed) return false;
      const query = this._measure.source.spec.query || "";
      return /from\s*\(\s*bucket\s*:/i.test(query) || /\|>/.test(query);
    },
    bucketFromQuery() {
      const query = this._measure.source.spec.query || "";
      const match = query.match(/from\s*\(\s*bucket\s*:\s*["']([^"']+)["']/i);
      return match ? match[1] : "";
    },
    pendingClearFieldLabels() {
      const labelMap = {
        "auth.username": this.$t("labels.username"),
        "auth.databaseName": this.$t("labels.database-name"),
        "spec.organization": this.$t("labels.organization"),
        "spec.bucket": this.$t("labels.bucket"),
      };
      return this.pendingClearFields.map((f) => labelMap[f] || f);
    },
    cardHeight() {
      return "calc(" + this.containerHeight + " - 64px - 40px)";
    },
    _measure() {
      return this.measure;
    },
    selectedType: {
      get() {
        return this._measure.source.type;
      },
      set(newValue) {
        this._measure.source.type = newValue.value.toLowerCase();
      },
    },
    jsonPathWithPrefix: {
      get() {
        return this._measure.source.spec.jsonPath?.replace(/^\$\./, "");
      },
      set(newValue) {
        this._measure.source.spec.jsonPath = `$.${newValue}`;
      },
    },
    disabledJsonPath() {
      if (
        !this.selectedType ||
        !this._measure.name ||
        !this._measure.context ||
        !this.protocol ||
        !this.uriWithPrefix
      ) {
        return true;
      }

      if (this.selectedType != "rest" && this.selectedType != "mongodb") {
        return true;
      }

      if (
        this.selectedType == "mongodb" &&
        (!this._measure.source.spec.collectionName ||
          !this._measure.source.auth.databaseName)
      ) {
        return true;
      }

      if (this.selectedType == "rest" && !this._measure.source.spec.method) {
        return true;
      }

      if (
        this.switchStatus &&
        (!this._measure.source.auth.username ||
          !this._measure.source.auth.accessKey)
      ) {
        return true;
      }

      return false;
    },

    queryWithSelectPrefix: {
      get() {
        return this._measure.source.spec.query.replace(/^SELECT\s/, "");
      },
      set(newValue) {
        this._measure.source.spec.query = `SELECT ${newValue}`;
      },
    },

    queryWithMatchPrefix: {
      get() {
        return this._measure.source.spec.query.replace(/^MATCH\s/, "");
      },
      set(newValue) {
        this._measure.source.spec.query = `MATCH ${newValue}`;
      },
    },

    uriWithPrefix: {
      get() {
        return this._measure.source.uri?.replace(
          /(http|https|mysql|postgres|bolt|bolt\+s|neo4j|neo4j\+s|mongodb):\/\//g,
          "",
        );
      },
      set(newValue) {
        const cleanedValue = newValue.replace(
          /(http|https|mysql|postgres|bolt|bolt\+s|neo4j|neo4j\+s|mongodb):\/\//g,
          "",
        );
        this._measure.source.uri = `${this.uriPrefix()}${cleanedValue}`;
      },
    },
  },

  methods: {
    checkHideDetails() {
      return this.nameRules.length === 0 && this.requiredRule.length === 0;
    },

    setProtocols(type) {
      switch (type) {
        case "postgres":
          this.protocols = ["HTTPS", "HTTP", "POSTGRES"];
          break;
        case "neo4j":
          this.protocols = ["BOLT", "BOLT+S", "NEO4J", "NEO4J+S"];
          break;
        case "mongodb":
          this.protocols = ["TCP"];
          break;
        case "mysql":
          this.protocols = ["HTTPS", "HTTP", "MYSQL"];
          break;
        default:
          this.protocols = ["HTTPS", "HTTP"];
          break;
      }
      this.protocol = this.protocols[0];
    },

    uriPrefix() {
      const type = this._measure.source?.type;
      if (this.protocol === "TCP") {
        switch (type) {
          case "mongodb":
            return "mongodb://";
        }
      } else if (this.protocol === "BOLT") {
        return "bolt://";
      } else if (this.protocol === "NEO4J") {
        return "neo4j://";
      } else if (this.protocol === "HTTP") {
        return "http://";
      } else if (this.protocol === "HTTPS") {
        return "https://";
      } else if (this.protocol === "POSTGRES") {
        return "postgres://";
      } else if (this.protocol === "BOLT+S") {
        return "bolt+s://";
      } else if (this.protocol === "NEO4J+S") {
        return "neo4j+s://";
      } else if (this.protocol === "MYSQL") {
        return "mysql://";
      }
    },

    checkFormValidity() {
      const uriWithoutProtocol = this._measure.source.uri?.replace(
        this.uriPrefix(),
        "",
      );
      let isValid =
        this._measure.name &&
        this._measure.context &&
        this.selectedType &&
        uriWithoutProtocol;

      if (this.selectedType === "influx") {
        this.$emit("form-validity-changed", !!(isValid && this.isInfluxSpecValid()));
        return;
      }

      if (this.selectedType === "rest" && this.switchStatus) {
        isValid =
          isValid &&
          !!this._measure.source.auth.username &&
          !!this._measure.source.auth.accessKey;
      }
      const typeFields = {
        rest: ["method"],
        mongodb: ["collectionName", "databaseName"],
        default: ["query", "databaseName"],
      };
      let requiredFields =
        typeFields[this.selectedType] || typeFields["default"];

      requiredFields.forEach((field) => {
        isValid =
          isValid &&
          (this._measure.source.spec[field] ||
            this._measure.source.auth[field]);
      });

      // For rest and mongodb types, jsonPath is mandatory:
      // it must be non-empty AND pass the remote validation before preview is enabled.
      // For other types (no jsonPath section rendered) this block is skipped.
      const jsonPathRequiredTypes = ["rest", "mongodb"];
      if (jsonPathRequiredTypes.includes(this.selectedType)) {
        isValid = isValid && !!this.jsonPathWithPrefix && this.jsonPathValidationStatus === true;
      }

      this.$emit("form-validity-changed", isValid);
    },

    isInfluxSpecValid() {
      const spec = this._measure.source.spec || {};
      const auth = this._measure.source.auth || {};

      if (!spec.influxVersion || !spec.influxQueryLanguage || !spec.query) {
        return false;
      }

      switch (spec.influxVersion) {
        case "v1":
          return !!auth.databaseName;
        case "v2":
          return (
            !!spec.organization &&
            !!auth.accessKey &&
            (spec.influxQueryLanguage === "Flux" ? true : !!spec.bucket)
          );
        case "v3":
          return !!auth.databaseName && !!auth.accessKey;
        default:
          return false;
      }
    },

    addRow() {
      const lastRow = this.headersRow[this.headersRow.length - 1];
      if (lastRow.key && lastRow.value) {
        this.headersRow.push({ key: "", value: "" });
      }
      this.transformHeaders(this.headersRow);
    },

    removeRow(index) {
      this.headersRow.splice(index, 1);
      this.transformHeaders(this.headersRow);
      if (this.headersRow.length === 0) {
        this.headersRow.push({ key: "", value: "" });
      }
    },

    onKeyChange(index, value) {
      this.headersRow[index].key = value;
      this.addRow();
    },

    onValueChange(index, value) {
      this.headersRow[index].value = value;
      this.addRow();
    },

    transformHeaders(headersRow) {
      let transformedHeaders = [];
      headersRow.forEach((header) => {
        if (header.key?.trim() !== "" && header.value?.trim() !== "") {
          let transformedHeader = `${header.key}:${header.value}`;
          transformedHeaders.push(transformedHeader);
        }
      });
      let headersString = transformedHeaders.join(", ");
      this._measure.source.spec.headers = headersString;
    },

    parseHeaders(headersString) {
      let headersArray = headersString.split(", ");
      let headersObjects = headersArray.map((headerString) => {
        let [key, value] = headerString.split(":");
        return { key, value };
      });
      headersObjects.push({ key: "", value: "" });
      this.headersRow = headersObjects;
    },

    toggleSwitch() {
      this.switchStatus = !this.switchStatus;
      if (this.switchStatus) {
        this._measure.source.auth.username = "";
        this._measure.source.auth.accessKey = "";
      } else {
        this._measure.source.auth.username =
          this.measure.source.auth?.username || "";
        this._measure.source.auth.accessKey =
          this.measure.source.auth?.accessKey || "";
      }
    },

    setCredentialsSwitchStatus() {
      if (this.measure.source.auth == null) {
        this._measure.source.auth = {
          username: "",
          databaseName: "",
          accessKey: "",
          secretPath: "",
        };
      }
      this.switchStatus = !!(
        (this.measure.source.auth?.username &&
          this.measure.source.auth?.accessKey) ||
        this.measure.source.auth?.accessKey
      );

      this.normalizeInfluxSelection();
      this.enforceInfluxSwitch();
    },

    enforceInfluxSwitch() {
      if (this.credentialsMandatory) {
        this.switchStatus = true;
      }
    },

    normalizeInfluxSelection() {
      if (this.measure.source.type !== "influx") {
        return;
      }

      const spec = this._measure.source.spec;
      const auth = this._measure.source.auth || {};

      if (spec.influxVersion) {
        spec.influxVersion = String(spec.influxVersion).toLowerCase();
      }
      spec.influxVersion = spec.influxVersion || "v2";

      if (!spec.organization && auth.organization) {
        spec.organization = auth.organization;
      }
      if (!spec.bucket && auth.bucket) {
        spec.bucket = auth.bucket;
      }
      if (!auth.accessKey && auth.token) {
        auth.accessKey = auth.token;
      }

      const compatibleLanguages =
        INFLUX_QUERY_LANGUAGE_COMPATIBILITY[spec.influxVersion] || [];

      if (!compatibleLanguages.includes(spec.influxQueryLanguage)) {
        spec.influxQueryLanguage = compatibleLanguages[0] || "";
      }

      (INFLUX_BLOCKED_FIELDS[spec.influxVersion] || []).forEach((path) =>
        this.setInfluxFieldValue(path, ""),
      );
    },

    requestInfluxVersionChange(newVersion) {
      const currentVersion = this._measure.source.spec.influxVersion;
      if (!newVersion || newVersion === currentVersion) {
        return;
      }

      const clearFields =
        INFLUX_VERSION_TRANSITIONS[`${currentVersion}->${newVersion}`] || [];
      const fieldsWithValue = clearFields.filter((path) =>
        this.getInfluxFieldValue(path),
      );

      if (fieldsWithValue.length === 0) {
        this.applyInfluxVersionChange(newVersion, clearFields);
        return;
      }

      this.pendingInfluxVersion = newVersion;
      this.pendingClearFields = fieldsWithValue;
      this.versionChangeDialog = true;
    },

    confirmVersionChange() {
      const currentVersion = this._measure.source.spec.influxVersion;
      const clearFields =
        INFLUX_VERSION_TRANSITIONS[
          `${currentVersion}->${this.pendingInfluxVersion}`
        ] || [];
      this.applyInfluxVersionChange(this.pendingInfluxVersion, clearFields);
      this.closeVersionChangeDialog();
    },

    cancelVersionChange() {
      this.versionSelectKey += 1;
      this.closeVersionChangeDialog();
    },

    closeVersionChangeDialog() {
      this.versionChangeDialog = false;
      this.pendingInfluxVersion = null;
      this.pendingClearFields = [];
    },

    applyInfluxVersionChange(newVersion, clearFields) {
      clearFields.forEach((path) => this.setInfluxFieldValue(path, ""));
      this._measure.source.spec.influxVersion = newVersion;
      const compatibleLanguages =
        INFLUX_QUERY_LANGUAGE_COMPATIBILITY[newVersion] || [];
      this._measure.source.spec.influxQueryLanguage =
        compatibleLanguages[0] || "";
      this.enforceInfluxSwitch();
      this.checkFormValidity();
    },

    getInfluxFieldValue(path) {
      const [group, key] = path.split(".");
      return this._measure.source[group]?.[key];
    },

    setInfluxFieldValue(path, value) {
      const [group, key] = path.split(".");
      if (this._measure.source[group]) {
        this._measure.source[group][key] = value;
      }
    },

    checkMandatoryFieldsForJsonpath() {
      if (!this.isInitialized || this.confirmJsonpathDeletion) return;

      if (this.jsonPathWithPrefix) {
        this.openDialogDeleteJsonpathConfirm();
      }
    },

    openDialogJsonProperty() {
      this.confirmJsonpathDeletion = undefined;
      this.$emit("openDialogJsonProperty");
    },

    openDialogDeleteJsonpathConfirm() {
      this.dialogConfirmJsonpathDeletion = true;
    },

    deleteJsonpathConfirm(val) {
      this.confirmJsonpathDeletion = val ? "confirm" : "no-confirm";
      if (val) {
        this.jsonPathWithPrefix = "";
        this.jsonPathValidationStatus = null;
        this.checkFormValidity();
      }
    },

    setDefaultContextForEditor() {
      if (this.isEditor && this.contextTypes.length > 0) {
        if (!this._measure.context || Object.keys(this._measure.context).length === 0) {
          this._measure.context = this.contextTypes[0];
        }
      }
    },

    // ─── JsonPath Validation ────────────────────────────────────────────────

    /**
     * Schedules a debounced call to validateJsonPath (400 ms).
     * Clears any previously scheduled timer.
     */
    _scheduleJsonPathValidation() {
      clearTimeout(this.jsonPathValidationTimer);
      this.jsonPathValidationTimer = setTimeout(() => {
        this.validateJsonPath();
      }, 400);
    },

    /**
     * Fetches the source's JSON response through the backend service
     * (POST measures/fetch?fullJsonResponse=true — the same one used to
     * open the JSON property picker) and evaluates the stored jsonPath
     * against it using jsonpath-plus, which supports the full JSONPath
     * spec including wildcards ([*]), recursive descent (..), filters, etc.
     * Updates jsonPathValidationStatus and re-checks overall form validity.
     *
     * Going through the backend (instead of fetching the source URI
     * directly from the browser) avoids CORS issues and reuses the same
     * server-side auth/credential handling already used elsewhere.
     */
    async validateJsonPath() {
      if (!this.jsonPathWithPrefix || !this._measure.source.uri) {
        this.jsonPathValidationStatus = null;
        this.checkFormValidity();
        return;
      }

      this.jsonPathValidating = true;
      this.jsonPathValidationStatus = null;
      this.jsonPathRateLimited = false;

      try {
        const cleanedForm = JSON.parse(JSON.stringify(this._measure));
        this._cleanObject(cleanedForm);
        if (cleanedForm?.source?.spec?.jsonPath) {
          delete cleanedForm.source.spec.jsonPath;
        }

        const response = await axios.post(
          `${this.apiUrl}/${this.realm}/measures/fetch?fullJsonResponse=true`,
          { ...cleanedForm },
          { withCredentials: true }
        );

        const json = response?.data?.data;
        if (!json) {
          this.jsonPathValidationStatus = false;
          return;
        }

        // Build the full JSONPath expression.
        // If the stored value already starts with "$." or "$[" leave it as-is,
        // otherwise prepend "$.".
        const rawPath = this.jsonPathWithPrefix;
        const fullPath = /^\$[.[[]/.test(rawPath) ? rawPath : `$.${rawPath}`;

        // Dynamically import jsonpath-plus so Vite can tree-shake it properly.
        const { JSONPath } = await import("jsonpath-plus");

        const results = JSONPath({ path: fullPath, json, wrap: true });

        // The path is valid when jsonpath-plus returns at least one non-null result.
        this.jsonPathValidationStatus =
          Array.isArray(results) &&
          results.length > 0 &&
          results.some((r) => r !== undefined && r !== null);
      } catch (error) {
        // Any HTTP error (404, 429, 500, ...) invalidates the current check
        // and is forwarded to App.vue's centralized error handling, instead
        // of being silently swallowed for anything other than 404.
        if (error.response) {
          this.jsonPathValidationStatus = false;
          if (error.response.status === 429) {
            // Lets the user retry immediately from the field itself instead
            // of having to leave and re-enter the form.
            this.jsonPathRateLimited = true;
          }
          this.$emit("error", error);
        }
      } finally {
        this.jsonPathValidating = false;
        this.checkFormValidity();
      }
    },

    // Recursively strips empty strings, nulls, and empty objects — mirrors
    // App.vue's cleanObject so the payload sent to measures/fetch matches
    // the shape the backend already expects (same as when opening the
    // JSON property picker).
    _cleanObject(obj) {
      for (let key in obj) {
        if (obj[key] && typeof obj[key] === "object") {
          this._cleanObject(obj[key]);
          if (Object.keys(obj[key]).length === 0) {
            delete obj[key];
          }
        } else if (obj[key] === "" || obj[key] === null) {
          delete obj[key];
        }
      }
    },

  },
};
</script>